import { ReactNode, useEffect, useRef, useState } from 'react';
import styled, { CSSProperties } from 'styled-components';

import { BodilColor } from '~src/branding';
import { LeadOrigin, useLeadOrigin } from '~src/hooks/useLeadOrigin';
import { useAnalytics } from '~src/tracking/ga';

import { Loader } from '../Loader';

export type BookingFormVariant = 'default' | 'offerIdentity';

type BookingFormProps = { title?: string | ReactNode; style?: CSSProperties; variant?: BookingFormVariant };

// The booking widget (PublicBookingWidget in comfort-energy-flow) is served
// from ops.bodil.energy, the same origin the marketing site's kontakt page
// embeds. The bare bodil.energy domain is the marketing site itself, which
// 404s on /book/embed and refuses framing.
const BOOKING_WIDGET_URL = 'https://ops.bodil.energy/book/embed';
const BOOKING_WIDGET_ORIGIN = 'https://ops.bodil.energy';
const MIN_HEIGHT = 480;

// Lead origins on the booking widget's partner allow-list (booking-create edge
// function in comfort-energy-flow). Anything else books as plain Bodil.
const PARTNER_SLUGS: Record<string, string | undefined> = {
  nordea: 'nordea',
  edc: 'edc',
  jyskebank: 'jyske-bank',
  norlys: 'norlys',
};

const BookingFormWrapper = styled.div<{ style?: CSSProperties; variant?: BookingFormVariant }>(
  ({ style, variant }) => ({
    width: '100%',
    maxWidth: '1040px',
    ...(variant === 'offerIdentity'
      ? {
          alignSelf: 'stretch',
          boxSizing: 'border-box',
          borderRadius: '22px',
          padding: '28px',
          background: `linear-gradient(180deg, ${BodilColor.base.white} 0%, #EAFBF5 100%)`,
          border: `1px solid ${BodilColor.ui.action['20']}`,
          boxShadow: '0 14px 40px rgba(1, 64, 65, 0.1)',
        }
      : {}),
    ...style,
  })
);

const WidgetFrame = styled.div<{ variant?: BookingFormVariant }>(({ variant }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: variant === 'offerIdentity' ? '18px' : '20px',
  marginTop: variant === 'offerIdentity' ? '20px' : '24px',
  minHeight: `${MIN_HEIGHT}px`,
  border: variant === 'offerIdentity' ? `1px solid ${BodilColor.ui.action['20']}` : undefined,
  background: variant === 'offerIdentity' ? BodilColor.base.white : undefined,
  // The widget card paints no shadow in embed mode (inside the iframe it can
  // only render as square-clipped haze around the rounded card); the host
  // paints it here instead, matching the marketing site's kontakt page.
  boxShadow: variant === 'offerIdentity' ? '0 14px 40px rgba(1, 64, 65, 0.1)' : '0 6px 32px rgba(1, 52, 51, 0.08)',
}));

const LoaderOverlay = styled.div({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'white',
  zIndex: 1,
});

const StyledIframe = styled.iframe({
  width: '100%',
  border: 0,
  display: 'block',
  background: 'transparent',
  transition: 'height 0.3s ease',
});

export const BookingForm = ({ title, style, variant = 'default' }: BookingFormProps) => {
  const { origin } = useLeadOrigin();

  const { trackEvent } = useAnalytics();

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [height, setHeight] = useState(MIN_HEIGHT);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== BOOKING_WIDGET_ORIGIN) return;

      // Desktop and mobile estimate variants both mount a BookingForm; only
      // react to the iframe that actually sent the message.
      if (event.source !== iframeRef.current?.contentWindow) return;

      if (event.data?.type === 'bodil-booking-height' && typeof event.data.h === 'number') {
        setHeight(Math.max(MIN_HEIGHT, Math.ceil(event.data.h)));
        setLoading(false);
      }

      if (event.data?.type === 'bodil-booking-completed') {
        trackEvent('estimate_page_booking_completed');
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [trackEvent]);

  const bookingUrl = getBookingUrl(origin);

  return (
    <BookingFormWrapper style={style} variant={variant}>
      {title}

      <WidgetFrame variant={variant}>
        {loading && (
          <LoaderOverlay>
            <Loader />
          </LoaderOverlay>
        )}

        <StyledIframe
          ref={iframeRef}
          title='Book møde med Bodil Energi'
          src={bookingUrl}
          loading='lazy'
          onLoad={() => setLoading(false)}
          style={{ height: `${height}px` }}
        />
      </WidgetFrame>
    </BookingFormWrapper>
  );
};

const getBookingUrl = (leadOrigin?: LeadOrigin) => {
  const partner = leadOrigin ? PARTNER_SLUGS[leadOrigin] : undefined;

  return partner ? `${BOOKING_WIDGET_URL}?partner=${partner}` : BOOKING_WIDGET_URL;
};
